/**
 * Parser Selector - Dropdown for selecting log parser type
 */

import React, { useState, useCallback, useRef, useEffect } from "react";
import { LogParserType, LOG_PARSER_LABELS } from "./types";
import { parsersForChannel, parserIsFixed } from "./channels";

export interface ParserSelectorProps {
  parserType: LogParserType;
  onParserChange: (parser: LogParserType) => void;
  /**
   * The channel being viewed. It decides which parsers can apply at all (see
   * channels.ts — the one table): a channel whose records are structured on the wire
   * admits exactly one, so there is nothing to choose and this goes inert. The picker
   * used to offer all five for every channel and then have the choice silently
   * dropped, because LogParsingPipeline routes on the buffer's SOURCE.
   */
  channel?: string;
}

const PARSER_DESCRIPTIONS: Record<LogParserType, string> = {
  'structured': 'Parse logs with timestamps, levels, threads, and categories',
  'applog': 'Structured records — level, caller, thread and exceptions are fields, not parsed text',
  'plain-text': 'Display each line as-is with no parsing',
  'journal': 'Parse systemd journald JSON records (one entry per record)',
  'eventlog': 'Parse Windows Event Log JSON records (one entry per record)',
};

function optionsFor(channel: string | undefined) {
  return parsersForChannel(channel).map((value) => ({
    value,
    label: LOG_PARSER_LABELS[value],
    description: PARSER_DESCRIPTIONS[value],
  }));
}

export function ParserSelector(props: ParserSelectorProps) {
  const { parserType, onParserChange, channel } = props;
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const PARSER_OPTIONS = optionsFor(channel);
  // Inert when the channel admits one parser (or none) — DERIVED from the table, not
  // a second flag anyone has to remember to set.
  const fixed = parserIsFixed(channel);

  // Close menu when clicking outside
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  const handleSelect = useCallback((parser: LogParserType) => {
    onParserChange(parser);
    setIsOpen(false);
  }, [onParserChange]);

  // resolveParserType guarantees parserType is one the channel admits, so this
  // lookup hits; LOG_PARSER_LABELS is the fallback rather than "Unknown".
  const currentOption = PARSER_OPTIONS.find(opt => opt.value === parserType);
  const buttonLabel = `Parser: ${currentOption?.label ?? LOG_PARSER_LABELS[parserType]}`;

  if (fixed) {
    // Nothing to choose. The channel's records are structured on the wire (fields, not
    // text), so no parser runs on them and there is no alternative to offer. Say so,
    // rather than accepting a selection we would then ignore.
    return (
      <div className="parser-selector">
        <button
          className="parser-selector__button parser-selector__button--disabled"
          disabled
          title={
            "This channel ships structured records (level, caller, thread and the " +
            "exception are fields on the wire — there is no text to parse), so no " +
            "parser applies. Use raw mode to see the writer's pre-rendered line."
          }
        >
          {buttonLabel}
        </button>
      </div>
    );
  }

  return (
    <div className="parser-selector" ref={menuRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="parser-selector__button"
        title={currentOption?.description}
      >
        {buttonLabel}
      </button>

      {isOpen && (
        <>
          <div className="parser-selector__backdrop" onClick={() => setIsOpen(false)} />
          <div className="parser-selector__menu">
            <div className="parser-selector__header">
              <span>Log Parser</span>
            </div>
            <div className="parser-selector__items">
              {PARSER_OPTIONS.map(option => (
                <button
                  key={option.value}
                  className={`parser-selector__item ${
                    parserType === option.value ? 'parser-selector__item--selected' : ''
                  }`}
                  onClick={() => handleSelect(option.value)}
                  title={option.description}
                >
                  <span>{option.label}</span>
                  {parserType === option.value && <span className="parser-selector__check">✓</span>}
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
